import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BulkSelectPayrollEmployeeDialogComponent } from '@app/components/dialogs/bulk-select-payroll-employee-dialog/bulk-select-payroll-employee-dialog.component';
import { BulkActionBarComponent, FileUploadComponent } from '@app/components/platform';
import { AnalyticEvents } from '@app/enums';
import { EmployeeBenefitBulkForm } from '@app/modules/forms/payroll/components';
import { PayrollBenefitsMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Employee } from '@models/employee/employee.model';
import { CompanyBenefit } from '@models/payroll/company/benefit.model';
import { EmployeeBenefitTable } from '@payroll/services';
import { BenefitEmployeesCsvService } from '@payroll/services/benefit-employees-csv.service';
import { TableComponent } from '@table/components';
import { BulkSelection } from '@table/types/bulk-selection.type';
import { EmployeeIndexService } from './employee-index.service';

@Component({
    templateUrl: './employee-index.template.html',
    providers: [EmployeeIndexService],
})
export class EmployeeIndexView {
    @ViewChild('bulkActionBar', { static: true }) bulkActionBar: BulkActionBarComponent;
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectPayrollEmployeeDialogComponent;
    @ViewChild('tableComponent', { static: true }) tableComponent: TableComponent<EmployeeBenefitTable>;
    @ViewChild('fileUpload', { static: true }) fileUpload: FileUploadComponent;

    isLoading = false;
    table: EmployeeBenefitTable;
    benefit: CompanyBenefit;
    bulkSelection: BulkSelection;
    expandCSVImportDropdown = false;

    selectionCount = 0;

    private loadAllAssignedEmployeesPromise: Promise<void>;

    constructor(
        public auth: AuthService,
        public route: ActivatedRoute,
        private analytics: AnalyticService,
        private notify: NotifyService,
        private employeeIndexService: EmployeeIndexService,
        private csvService: BenefitEmployeesCsvService,
        public payrollBenefitsMenuService: PayrollBenefitsMenuService
    ) {
        this.benefit = this.route.snapshot.data.models.get('benefit');
        this.table = new EmployeeBenefitTable(this.auth, this.analytics, this.benefit.id);

        this.loadAllAssignedEmployees();
    }

    selectionChanged(bulkSelection: BulkSelection): void {
        this.bulkSelection = bulkSelection;

        if (!this.table.isEmpty && (bulkSelection.isAllSelected || bulkSelection.selectedIds.size)) {
            this.selectionCount = bulkSelection.selectionCount;
            this.bulkActionBar.open = true;
            return;
        }

        this.bulkActionBar.open = false;
    }

    async onAssign(): Promise<void> {
        this.isLoading = true;
        await this.loadAllAssignedEmployeesPromise;
        this.isLoading = false;

        const newSelection = await this.selectEmployees.show<Employee[]>(this.employeeIndexService.assignedEmployees);

        this.benefit.clearAttach().clearDetach();

        const [attach, detach] = this.employeeIndexService.determineAttachAndDetach(
            newSelection,
            this.employeeIndexService.assignedEmployees
        );

        this.benefit.attach('employees', attach).detach('employees', detach);

        this.isLoading = true;

        try {
            await this.benefit.param('company', this.auth.company.id).save();
        } catch (e) {
            this.notify.error(e);
        }

        this.table.reload();
        this.loadAllAssignedEmployees();

        this.isLoading = false;
    }

    onCsvTemplateDownload(): void {
        this.isLoading = true;
        this.csvService.downloadTemplate(this.benefit.id).finally(() => (this.isLoading = false));
    }

    async onCsvUpload(file?: File): Promise<void> {
        if (!file) {
            (document.querySelector('[name="fileSelector"]') as HTMLElement).click();
            return;
        }

        this.isLoading = true;

        try {
            await this.csvService.upload(this.benefit.id, file);

            this.table.reload();
            this.analytics.trackEvent(AnalyticEvents.PayrollBenefitsEmployeesUploadTemplateSave);
        } catch (e) {
            this.notify.error(e);
        }

        // Clear the input after uploading. This is the quick and dirty way, maybe down the road
        // we can add a method to the file drop component to take care of this for us.
        (document.querySelector('[name="fileSelector"]') as HTMLInputElement).value = '';

        this.isLoading = false;
    }

    async bulkAction(form: EmployeeBenefitBulkForm): Promise<void> {
        const benefitBulk = form.benefit;

        benefitBulk.bulkSelection = {
            ...this.bulkSelection,
            selectedIds: Array.from(this.bulkSelection.selectedIds),
            deselectedIds: Array.from(this.bulkSelection.deselectedIds),
        };

        this.isLoading = true;

        try {
            await benefitBulk.param('benefit', this.benefit.id).param('company', this.auth.company.id).save();

            if (benefitBulk.bulkSelection.selectionCount > 1) {
                this.analytics.trackEvent(AnalyticEvents.PayrollBenefitsEmployeesBulkAdjustmentSave);
            }

            form.reset();
            this.tableComponent.clearSelection();

            this.table.reload();
            this.loadAllAssignedEmployees();
        } catch (e) {
            this.notify.error(e);
        }

        this.bulkActionBar.open = false;
        this.isLoading = false;
    }

    private loadAllAssignedEmployees(): Promise<void> {
        this.loadAllAssignedEmployeesPromise = this.employeeIndexService.loadAllAssigned(this.benefit.id);

        return this.loadAllAssignedEmployeesPromise;
    }
}
